/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.21.0
 * @since 1.19.0
 */

import { Fragment, useEffect, useState } from 'react';

import { useDropzone } from 'react-dropzone';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../assets';
import { Button, InfoBlock, LinkBlock } from '../../../components/common';
import { ImgStyled } from '../../../components/common/Block/Img';
import { ELinkTarget } from '../../../components/common/ExternalLink/ExternalLink';
import { EColorStatus, EFileExtension, MapExtensionToIcon } from '../../../modules/app/appConstants';
import EFormValidation from '../../../modules/form/const/formValidation';
import { ELocaleKeys } from '../../../modules/internationalization/intConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardMultiSearch,
} from '../../../modules/internationalization/intSelectors';
import { ICommonTranslation, IWizardMultiSearchTranslation } from '../../../modules/internationalization/types';
import { IRootReducer } from '../../../reducers/rootReducer';
import { getFileExtension } from '../../../utils/formatText';
import { stringFormatParams, stringFormatParamsHtml } from '../../../utils/stringFormat';
import { wizardMultiSearchAttachFile } from '../actions';
import { IWizardMultiSearchReducer } from '../reducer';
import {
    makeSelectWizardMultiSearchFileError,
    makeSelectWizardMultiSearchFileName,
    makeSelectWizardMultiSearchIsFileLoading,
    makeSelectWizardMultiSearchStepSelection,
} from '../selectors';
import { IWizardMultiSearchAttachFilePayload, IWizardMultiSearchStepSelection } from '../types';

const WizardMultiSearchStepSelection = ({
                                            attachFile,
                                            commonTranslation,
                                            fileError,
                                            fileIsLoading,
                                            fileName,
                                            stepData,
                                            wizardTranslation,
                                        }: IConnectedProps & IDispatchProps) => {

    const { MsisdnDocumentSelectionStep: tStep } = wizardTranslation;

    const [fileValidationError, setFileValidation] = useState<string | undefined>();

    useEffect(() => {
        setFileValidation(fileError);
    }, [fileError]);


    const onDropHandler = (acceptedFiles: File[]) => {
        setFileValidation(undefined);
        attachFile({ file: acceptedFiles[0] });
    };

    const { getInputProps, open } = useDropzone({
        onDrop: onDropHandler,
        multiple: false,
        noClick: false,
        noKeyboard: false,
    });

    return (
        <div>
            <div className="wizard-multi-search__text_bold">
                {tStep.title}
            </div>
            <div style={{ margin: '24px 0', maxWidth: 700 }}>
                <InfoBlock colorStatus={EColorStatus.WARNING}>
                    {tStep.info}
                </InfoBlock>
            </div>
            {stringFormatParamsHtml(tStep.template, {
                [ELocaleKeys.format]: stepData.templateUrl
                    ? <LinkBlock isExternalUrl target={ELinkTarget.BLANK} url={stepData.templateUrl}>
                        {tStep.templateUrl}
                    </LinkBlock>
                    : <Fragment>{tStep.templateUrl}</Fragment>,
            })}
            <div style={{ whiteSpace: 'pre-line' }}>
                {stringFormatParams(tStep.maxImportSubs, {
                    [ELocaleKeys.number]: stepData.maxRows,
                })}
            </div>
            <div className="wizard-multi-search__text_bold" style={{ marginBottom: 24 }}>
                {tStep.warning}
            </div>

            <div style={{ marginBottom: 24 }}>
                <Button
                    dataName="wizard-multi-search-choose-file-btn"
                    kind="medium"
                    onClick={open}
                >
                    {commonTranslation.form.attachFile}
                </Button>
            </div>
            {
                fileIsLoading && <ImgStyled className="medium" src={icons.searchIndicator} style={{ width: 32 }}/>
            }
            {
                !fileIsLoading && fileName && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <ImgStyled
                            className="medium"
                            src={MapExtensionToIcon[getFileExtension(fileName) as EFileExtension]
                            ?? icons.defaultFileIcon}
                        />
                        <div>{fileName}</div>
                    </div>
                )
            }
            {
                fileValidationError && (
                    <div
                        className="wizard-multi-search__text_error"
                        data-name="wizard-multi-search-file-error"
                    >
                        {
                            fileValidationError || commonTranslation.form.validation[EFormValidation.fileNotFound]
                        }
                    </div>
                )
            }
            {/* Hidden input */}
            <input
                {...getInputProps()}
                data-name="wizard-multi-search-choose-file-input"
            />
        </div>
    );
};

interface IConnectedProps extends Pick<IWizardMultiSearchReducer, 'fileIsLoading' | 'fileError' | 'fileName'> {
    stepData: IWizardMultiSearchStepSelection;
    commonTranslation: ICommonTranslation;
    wizardTranslation: IWizardMultiSearchTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    commonTranslation: makeSelectTranslationCommon(),
    fileError: makeSelectWizardMultiSearchFileError(),
    fileIsLoading: makeSelectWizardMultiSearchIsFileLoading(),
    fileName: makeSelectWizardMultiSearchFileName(),
    stepData: makeSelectWizardMultiSearchStepSelection(),
    wizardTranslation: makeSelectTranslationWizardMultiSearch(),
});

export interface IDispatchProps {
    attachFile: (payload: IWizardMultiSearchAttachFilePayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    attachFile: (payload: IWizardMultiSearchAttachFilePayload) =>
        dispatch(wizardMultiSearchAttachFile(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardMultiSearchStepSelection);
